import { useContext, useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import { PendingStatusIndicator, DatasetStatus, PendingStatus } from '@components'
import { useDispatch, useSelector } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { PageHeaderContext } from '@contexts/pageHeader'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import * as DatasetActions from '@datasets/datasets.actions'
import { ReadGrid } from '@datasets/handsontable/read-grid/read-grid'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { ApprovalActionButton, CommentDialog, ConfirmDialog } from './components'
import * as css from './styles'
import { useApprovalContext, useCommentsContext } from '../../contexts'

export enum ApprovalAction {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  CANCEL = 'CANCEL',
}

export const ReviewApprovalRequest = () => {
  const dispatch = useDispatch()
  const { reviewingRequest } = useApprovalContext() ?? { reviewingRequest: null }
  const { rows, columns, commentCount } = useCommentsContext()
  const { datasetLoading, limit, setDatasetLoading } = useDatasetQueryContext()
  const { dispatch: pageHeaderDispatch } = useContext(PageHeaderContext)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [action, setAction] = useState<ApprovalAction | null>(null)
  const meta = useAppSelector(
    state => state.Datasets.datasetMetadataById[reviewingRequest!.dataset_id],
  )
  const template = useAppSelector(state => state.Submissions.templatesById[meta?.id])
  const [DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )

  useEffect(() => {
    setDatasetLoading(true)
    void updateDatasetMetadata()

    async function updateDatasetMetadata() {
      const datasetMetadata = await api.get(
        `/api/v1/account/dataset/${reviewingRequest!.dataset_id}/metadata`,
      )
      dispatch(DatasetActions.setDatasetMetadata(datasetMetadata.data))
      setDatasetLoading(false)
    }
  }, [reviewingRequest!.dataset_id])

  // Set page header configuration
  useEffect(() => {
    const folderName = template?.folder?.name
    const templateName = meta?.template_name

    if (!folderName && !templateName) return

    pageHeaderDispatch({
      type: 'setBreadcrumbs',
      payload: [
        ...(DATASETS_PAGE_ENABLED
          ? [{ label: 'Datasets', path: '/' }]
          : [{ label: 'Templates', path: '/codes/templates' }]),
        folderName ? { label: folderName } : null,
        { label: templateName, path: null },
      ].filter(p => p),
    })

    return () => {
      pageHeaderDispatch({
        type: 'setBreadcrumbs',
        payload: [],
      })
    }
  }, [meta?.template_name, template?.folder?.name])

  const rejectDisabled = commentCount === 0
  const acceptDisabled = datasetLoading || commentCount > 0

  return (
    <css.Container>
      <css.Topbar>
        <css.FlexDiv>
          <Typography css={css.title} variant="h4" gutterBottom>
            {reviewingRequest!.submission_name}
          </Typography>
          <PendingStatusIndicator
            status={reviewingRequest!.status as typeof DatasetStatus.PENDING | PendingStatus}
          />
        </css.FlexDiv>
        <css.ButtonContainer>
          <ApprovalActionButton
            contained={false}
            isLoading={datasetLoading}
            buttonText="Cancel"
            handleClick={handleClickCancel}
          />
          <ApprovalActionButton
            isLoading={datasetLoading}
            disabled={rejectDisabled}
            tooltipTitle="To reject changes, please add comments to at least one cell below"
            buttonText="Reject"
            handleClick={handleClickReject}
          />
          <ApprovalActionButton
            isLoading={datasetLoading}
            disabled={acceptDisabled}
            tooltipTitle="To accept, you must remove all comments"
            buttonText="Accept"
            handleClick={handleClickAccept}
          />
        </css.ButtonContainer>
      </css.Topbar>
      <css.ReadGridContainer>
        <ReadGrid
          key={reviewingRequest!.dataset_id}
          columns={(columns as any) ?? undefined}
          limit={limit}
          loading={datasetLoading}
          meta={meta}
          rows={rows}
        />
      </css.ReadGridContainer>
      <CommentDialog />
      <ConfirmDialog action={action} open={confirmDialogOpen} setOpen={setConfirmDialogOpen} />
    </css.Container>
  )

  function handleClickCancel() {
    setConfirmDialogOpen(true)
    setAction(ApprovalAction.CANCEL)
  }

  function handleClickReject() {
    setConfirmDialogOpen(true)
    setAction(ApprovalAction.REJECT)
  }

  function handleClickAccept() {
    setConfirmDialogOpen(true)
    setAction(ApprovalAction.ACCEPT)
  }
}
